import styled from 'styled-components';

import AgreementCheck from '@/components/atom/icon/AgreementCheck';
import KeyboardArrowRight from '@/components/atom/icon/KeyboardArrowRight';
import BottomSheet from '@/components/molecules/bottom-sheet';
import { PolicyAgreementBottomSheetViewModel } from '@/hooks/qna/mobile/write/usePolicyAgreementBottomSheetViewModel';
import { Button } from 'doctornow-design-system';

interface Props {
  viewModel: PolicyAgreementBottomSheetViewModel;
  afterSubmit?: () => void;
  isSubmitButtonDisabled: boolean;
}

const PolicyAgreementBottomSheet = ({
  viewModel: {
    isBottomSheetOpened,
    checkAll,
    isAllChecked,
    policyList,
    checkPolicy,
    isAllRequiredChecked,
    isPolicyChecked,
    closeBottomSheet,
    agreeToPolicies,
    openPolicyDetail,
  },
  afterSubmit,
  isSubmitButtonDisabled,
}: Props) => {
  const onSubmit = async () => {
    await agreeToPolicies();
    afterSubmit?.();
  };
  return (
    <BottomSheet index={10} isOpened={isBottomSheetOpened()} onClickOverlay={closeBottomSheet}>
      <Wrapper>
        <TitleAndDescription>
          <Title>상담을 위해 동의가 필요해요.</Title>
          <Description>
            {'의료 상담 특성 상, 아래의 약관 동의가 필요해요.\n동의해주신만큼 도움되는 정보를 제공해드릴게요.'}
          </Description>
        </TitleAndDescription>
        <PolicyAgreementForm>
          <AgreeToAllButton onClick={checkAll}>
            <AgreementCheckButton checked={isAllChecked()} />
            전체 동의
          </AgreeToAllButton>
          <PolicyList>
            {policyList.map((policy) => (
              <PolicyRow key={`policy-${policy.title}`} onClick={() => checkPolicy(policy)}>
                <CheckAndPolicyText>
                  <AgreementCheckButton checked={isPolicyChecked(policy)} />
                  {`[${policy.required ? '필수' : '선택'}] ${policy.title}`}
                </CheckAndPolicyText>
                <button onClick={() => openPolicyDetail(policy)}>
                  <KeyboardArrowRight />
                </button>
              </PolicyRow>
            ))}
          </PolicyList>
        </PolicyAgreementForm>
        <Button
          size={'xl'}
          className={'w-full'}
          onClick={onSubmit}
          disabled={!isAllRequiredChecked() || isSubmitButtonDisabled}
        >
          확인
        </Button>
      </Wrapper>
    </BottomSheet>
  );
};

export default PolicyAgreementBottomSheet;

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  padding: 12px 24px 24px 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
`;

const TitleAndDescription = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const Title = styled.div`
  display: flex;
  align-items: flex-start;
  align-self: stretch;
  ${({ theme }) => `
    ${theme.typography.UI20SB};
    color: ${theme.color.gray900};
  `};
`;

const Description = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  white-space: pre-wrap;
  ${({ theme }) => `
    ${theme.typography.UI14M};
    color: ${theme.color.gray500};
  `}
`;

const PolicyAgreementForm = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
`;

const AgreeToAllButton = styled.div`
  display: flex;
  height: 48px;
  padding: 0px 16px;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  ${({ theme }) => `
    border-radius: 8px;
    border: 1px solid ${theme.color.gray100};
    
    ${theme.typography.UI16M};
    color: ${theme.color.gray600};   
  `}
`;

const PolicyList = styled.div`
  display: flex;
  padding: 0px 10px 16px 20px;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
`;

const PolicyRow = styled.div`
  display: flex;
  padding: 8px 0px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

const CheckAndPolicyText = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1 0 0;

  ${({ theme }) => `
    ${theme.typography.UI16R};
    color: ${theme.color.gray900};
  `};
`;

const AgreementCheckButton = styled(AgreementCheck).attrs<{
  checked: boolean;
}>(({ checked, theme }) => ({
  color: checked ? theme.color.primary600 : theme.color.gray100,
}))<{ checked: boolean }>``;
